import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Medal,
  User,
  Target,
  AlertCircle,
  Trophy,
  Calendar,
  PoundSterling,
  ArrowUpRight,
  ArrowDownRight,
  Square,
  Repeat
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_OPTIONS = [
  { value: 'goals', label: 'Goals', icon: Target },
  { value: 'assists', label: 'Assists', icon: Target },
  { value: 'training_attendance', label: 'Training Attendance', icon: Calendar },
  { value: 'player_dues_paid', label: 'Player Dues Paid (£)', icon: PoundSterling },
  { value: 'starts', label: 'Starts', icon: User },
  { value: 'appearances', label: 'Appearances', icon: User },
  { value: 'player_of_match', label: 'Player of Match', icon: Trophy },
  { value: 'minutes_played', label: 'Minutes Played', icon: Target },
  { value: 'subbed_on', label: 'Most Subbed On', icon: ArrowUpRight },
  { value: 'subbed_off', label: 'Most Subbed Off', icon: ArrowDownRight },
  { value: 'unused_sub', label: 'Most Unused Sub', icon: Square },
  { value: 'sub_total', label: 'Most Sub Total', icon: Repeat },
  { value: 'yellow_cards', label: 'Yellow Cards', icon: AlertCircle },
  { value: 'red_cards', label: 'Red Cards', icon: AlertCircle }
];

export default function Leaderboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statType, setStatType] = useState('goals');

  useEffect(() => {
    if (user?.current_season_id) {
      fetchLeaderboard();
    } else {
      setLoading(false);
    }
  }, [user?.current_season_id, statType]);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard?stat_type=${statType}`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankClass = (rank) => {
    switch (rank) {
      case 1: return 'rank-1';
      case 2: return 'rank-2';
      case 3: return 'rank-3';
      default: return 'rank-default';
    }
  };

  const getStatLabel = () => {
    return STAT_OPTIONS.find(opt => opt.value === statType)?.label || 'Value';
  };

  if (!user?.current_season_id) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="no-season-message">
          <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 font-title">No Season Selected</h2>
          <p className="text-gray-500 mb-6">Create a season first to view leaderboards.</p>
          <Button onClick={() => navigate('/seasons')} className="btn-primary rounded-xl">
            Go to Seasons
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="leaderboard-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">Leaderboard</h1>
            <p className="text-gray-500 mt-1 text-sm sm:text-base">Top performers across different statistics.</p>
          </div>
          <Select value={statType} onValueChange={setStatType}>
            <SelectTrigger className="w-full sm:w-48 rounded-xl" data-testid="stat-type-select">
              <SelectValue placeholder="Select stat" />
            </SelectTrigger>
            <SelectContent>
              {STAT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-2xl mx-auto" data-testid="top-3-podium">
            {/* 2nd Place */}
            <div className="flex flex-col items-center pt-6 sm:pt-8">
              <div className="relative">
                {leaderboard[1]?.photo ? (
                  <img src={leaderboard[1].photo} alt={leaderboard[1].name} className="w-14 h-14 sm:w-20 sm:h-20 rounded-full object-cover ring-4 ring-gray-300" />
                ) : (
                  <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-gray-200 flex items-center justify-center ring-4 ring-gray-300">
                    <User className="w-7 h-7 sm:w-10 sm:h-10 text-gray-500" strokeWidth={1.5} />
                  </div>
                )}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-400 flex items-center justify-center text-white font-bold text-xs sm:text-base shadow-lg">
                  2
                </div>
              </div>
              <h3 className="mt-3 sm:mt-4 font-semibold text-gray-900 text-center text-xs sm:text-base truncate max-w-full px-1">{leaderboard[1]?.name}</h3>
              <p className="text-lg sm:text-2xl font-bold text-gray-600">{leaderboard[1]?.value}</p>
            </div>

            {/* 1st Place */}
            <div className="flex flex-col items-center">
              <div className="relative">
                {leaderboard[0]?.photo ? (
                  <img src={leaderboard[0].photo} alt={leaderboard[0].name} className="w-18 h-18 sm:w-24 sm:h-24 rounded-full object-cover ring-4 ring-amber-400" />
                ) : (
                  <div className="w-18 h-18 sm:w-24 sm:h-24 rounded-full bg-amber-100 flex items-center justify-center ring-4 ring-amber-400" style={{width: '4.5rem', height: '4.5rem'}}>
                    <User className="w-9 h-9 sm:w-12 sm:h-12 text-amber-600" strokeWidth={1.5} />
                  </div>
                )}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold shadow-lg">
                  <Trophy className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2} />
                </div>
              </div>
              <h3 className="mt-3 sm:mt-4 font-semibold text-gray-900 text-center text-sm sm:text-lg truncate max-w-full px-1">{leaderboard[0]?.name}</h3>
              <p className="text-xl sm:text-3xl font-bold text-amber-600">{leaderboard[0]?.value}</p>
            </div>

            {/* 3rd Place */}
            <div className="flex flex-col items-center pt-10 sm:pt-12">
              <div className="relative">
                {leaderboard[2]?.photo ? (
                  <img src={leaderboard[2].photo} alt={leaderboard[2].name} className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover ring-4 ring-orange-300" />
                ) : (
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-orange-100 flex items-center justify-center ring-4 ring-orange-300">
                    <User className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" strokeWidth={1.5} />
                  </div>
                )}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-xs sm:text-sm shadow-lg">
                  3
                </div>
              </div>
              <h3 className="mt-3 sm:mt-4 font-semibold text-gray-900 text-center text-xs sm:text-base truncate max-w-full px-1">{leaderboard[2]?.name}</h3>
              <p className="text-base sm:text-xl font-bold text-orange-600">{leaderboard[2]?.value}</p>
            </div>
          </div>
        )}

        {/* Full Leaderboard */}
        <Card data-testid="full-leaderboard">
          <CardHeader>
            <CardTitle className="text-xl font-semibold font-title">
              {getStatLabel()} Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading leaderboard...</div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-12">
                <div className="empty-state-icon">
                  <Medal className="w-10 h-10" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Data Yet</h3>
                <p className="text-gray-500">Add players and record match statistics to see rankings.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {leaderboard.map((player) => (
                  <div key={player.id} className="leaderboard-row" data-testid={`leaderboard-row-${player.id}`}>
                    <div className={`leaderboard-rank ${getRankClass(player.rank)}`}>
                      {player.rank}
                    </div>
                    <div className="flex items-center gap-3 flex-1">
                      {player.photo ? (
                        <img src={player.photo} alt={player.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{player.name}</p>
                        <p className="text-sm text-gray-500">#{player.jersey_number} • {player.position}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-emerald-600">
                        {statType === 'player_dues_paid' ? `£${player.value.toFixed(2)}` : player.value}
                      </p>
                      <p className="text-xs text-gray-500">{getStatLabel()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
